import { useState, useRef, useEffect } from 'react';
import { isObject } from 'underscore';

import { UploadJob } from '@/store/slices/content/types';
import { GetUploadEndpoint, SetItemContentType, UploadFileToProvider } from '@ui/uploads/validations';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getUser } from '@/store/slices/user/selectors';
import { UploadVideo } from '@/connection/content/videos';
import toastTrigger from '@ui/toast/toast-trigger';
import { selectUploadJobs } from '@/store/slices/content/selectors';
import { finishUploadJob } from '@/store/slices/content';
import { getVideoDuration, getVideoThumbnail } from '@/micros/homepage/content/utils/upload-utils';
import { removeInvalidFilenameCharacters } from '../utils';

interface InputRefs {
  [key: string]: HTMLInputElement | null;
}

//TODO all commented code in this file is related to connecting socket messages

export interface UploadingJob extends UploadJob {
  progress: number;
  done: boolean;
  totalUploadedBytes: number;
  lastUploadedBytes: number;
  totalBytesToUpload: number;
  filename: string;
}

const useUploader = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const uploadJobs = useAppSelector(selectUploadJobs);
  // const activeChannel = useAppSelector(getActiveChannel);

  const [jobsInProgress, setJobsInProgress] = useState<UploadingJob[]>([]);

  const inputElements = useRef<InputRefs>({});
  // const socket = useRef<SocketConnection | null>(null);

  //TODO finish connecting and displaying conversion states
  // useEffect(() => {
  //   const channel = `admin-${activeChannel}`;
  //   if (activeChannel && user) {
  //     socket.current = socketManager.getFromHook(channel);
  //     socket.current.addListener('video-conversion-progress', ({ data }: { data: StaticVideo }) => {
  //       //TODO
  // 		});
  //     socket.current.addListener('video-conversion-complete', ({ data }: { data: StaticVideo }) => {
  //       //TODO
  // 		});
  //     socket.current.addListener('video-conversion-error', ({ data }: { data: StaticVideo }) => {
  //       //TODO
  // 		});
  //   }

  //   return () => {
  //     socketManager.leave(channel);
  //   };
  // }, [activeChannel, user]);

  useEffect(() => {
    const currentJobs = jobsInProgress.map((job) => job.uuid);

    // here we want to track the newly added ones so we can trigger their input elements
    const newJobs: UploadingJob[] = uploadJobs
      .filter((job) => !currentJobs.includes(job.uuid))
      .map((job) => {
        const uuid = job.uuid;

        // wait until after the next render to show the element
        setTimeout(() => {
          inputElements.current[uuid]?.click();
        }, 500);

        return {
          ...job,
          progress: 0,
          done: false,
          totalUploadedBytes: 0,
          lastUploadedBytes: 0,
          totalBytesToUpload: 0,
          filename: '',
        };
      });

    setJobsInProgress([...jobsInProgress, ...newJobs]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadJobs]);

  const handleUploadProgress = (uuid: string) => (total: number) =>
    setJobsInProgress((_jobs) =>
      _jobs.map((job) => {
        if (job.uuid === uuid) {
          job.totalUploadedBytes += total - job.lastUploadedBytes;
          job.progress = Math.floor((job.totalUploadedBytes / job.totalBytesToUpload) * 100);
          job.lastUploadedBytes = total;
        }
        return job;
      })
    );

  const handleFileChange =
    (job: UploadingJob) =>
    async ({ target: { files } }: React.ChangeEvent<HTMLInputElement>) => {
      let uuid: string;
      let filename = '';
      try {
        if (!files?.length || !user) return;

        const fileToUpload = files[0];
        uuid = job.uuid;
        const cleanedName = removeInvalidFilenameCharacters(fileToUpload.name);
        filename = fileToUpload.name;
        if (job.onStarted) job.onStarted();

        setJobsInProgress((jobsInProgress) =>
          jobsInProgress.map((_job) => {
            if (_job.uuid === uuid) {
              _job.uploading = true;
              _job.totalBytesToUpload = fileToUpload.size;
              _job.filename = cleanedName;
            }

            return _job;
          })
        );

        const duration = await getVideoDuration(fileToUpload);
        const endpoint = await GetUploadEndpoint({ filename: cleanedName });

        if (isObject(endpoint) && endpoint.url) {
          const finalUrl = await UploadFileToProvider(
            {
              url: endpoint.url,
              fields: endpoint.fields,
              cdnHost: endpoint?.cdnHost,
            },
            fileToUpload,
            handleUploadProgress(uuid),
            cleanedName
          );

          SetItemContentType({ filename: endpoint.fields.Key, contentType: fileToUpload.type }).catch(console.error);

          // Generate the thumbnail for the video
          const thumb = (await getVideoThumbnail(fileToUpload)) as Blob;
          const thumbFile = new File([thumb], 'thumbnail.jpg', { type: 'image/jpeg' });
          const filenameParts = thumbFile.name.split('.');
          const suffix = filenameParts[filenameParts.length - 1];
          const reader = new FileReader();
          reader.onload = () => {
            if (typeof reader.result === 'string') {
              // create an image element and load in the image in base64
              const image = document.createElement('img');
              image.onload = async () => {
                const filename = `${Date.now()}_w${image.width}_h${image.height}.${suffix}`;

                const thumbnailEndpoint = await GetUploadEndpoint({ filename: filename, contentType: 'image/jpeg' });

                if (isObject(thumbnailEndpoint) && thumbnailEndpoint.url) {
                  const thumbnailUrl = await UploadFileToProvider(
                    {
                      url: thumbnailEndpoint.url,
                      fields: thumbnailEndpoint.fields,
                      cdnHost: thumbnailEndpoint?.cdnHost,
                    },
                    thumb,
                    handleUploadProgress(uuid),
                    cleanedName
                  );

                  SetItemContentType({ filename: thumbnailEndpoint.fields.Key, contentType: 'image/jpeg' }).catch(
                    console.error
                  );
                  const createdVideo = await UploadVideo(user.active_channel, {
                    type: 'uploaded',
                    name: cleanedName,
                    source: finalUrl,
                    use_thumbnail: false,
                    image: thumbnailUrl,
                    original_image: thumbnailUrl,
                    duration: duration,
                  });

                  toastTrigger(
                    <div className="S1">
                      {
                        <div>
                          <div>{cleanedName} Uploaded</div>
                          <div>Please allow several minutes for the video to be available for playback.</div>
                        </div>
                      }
                    </div>,
                    {
                      type: 'info',
                      autoClose: 10000,
                    }
                  );

                  // call callback with the created video
                  setJobsInProgress((jobs) =>
                    jobs.map((job) => {
                      if (job.uuid === uuid) {
                        job.done = true;
                        job.onComplete(createdVideo);
                      }
                      return job;
                    })
                  );
                }
              };
              image.src = reader.result;
            }
          };

          reader.readAsDataURL(thumbFile);

          // allow "Done" message for 5 seconds, then remove from list
          setTimeout(() => {
            setJobsInProgress((jobs) =>
              jobs.filter((job) => {
                return job.uuid !== uuid;
              })
            );
            dispatch(finishUploadJob(uuid));
          }, 5000);
        }
      } catch (e) {
        console.error(e);
        setJobsInProgress((jobs) =>
          jobs.filter((_job) => {
            return _job.uuid !== uuid;
          })
        );

        toastTrigger(
          <div className="S1">
            {
              <div>
                <div>Upload error</div>
                <div>There was an error uploading {filename ? filename : 'your content'}, please try again.</div>
              </div>
            }
          </div>,
          {
            type: 'error',
            autoClose: 10000,
          }
        );
      }
    };

  return {
    jobsInProgress,
    inputElements,
    handleFileChange,
  };
};

export default useUploader;
